import type { MockCache, StorageFolder, StorageFolderId } from '../types';

import { SYSTEM_FOLDERS, isSystemFolderId } from './system';
import type { SystemFolder } from './system';

export interface StorageFolderInfo {
  id: StorageFolderId;
  parentId: StorageFolderId | null;
  name: string;
  system: boolean;
  personal: boolean;
}

export interface StorageFolderNode extends StorageFolderInfo {
  children: StorageFolderNode[];
}

function bySortKey(left: StorageFolder, right: StorageFolder): number {
  if (left.sortKey !== right.sortKey) {
    return left.sortKey < right.sortKey ? -1 : 1;
  }
  return left.id < right.id ? -1 : 1;
}

function systemInfo(folder: SystemFolder): StorageFolderInfo {
  return { id: folder.id, parentId: null, name: folder.name, system: true, personal: folder.personal };
}

function generalInfo(folder: StorageFolder): StorageFolderInfo {
  return { id: folder.id, parentId: folder.parentId, name: folder.name, system: false, personal: false };
}

export function storageFolderChildren(cache: MockCache, parentId: StorageFolderId | null): StorageFolder[] {
  const children: StorageFolder[] = [];
  for (const folder of cache.storageFolders.values()) {
    const inScope = parentId !== null || folder.workspaceId === cache.activeWorkspaceId;
    if (folder.parentId === parentId && inScope) {
      children.push(folder);
    }
  }
  return children.sort(bySortKey);
}

function generalNodes(cache: MockCache, parentId: StorageFolderId | null): StorageFolderNode[] {
  return storageFolderChildren(cache, parentId).map((folder) => ({
    ...generalInfo(folder),
    children: generalNodes(cache, folder.id),
  }));
}

export function selectStorageFolders(cache: MockCache): StorageFolderNode[] {
  const system = SYSTEM_FOLDERS.map((folder) => ({ ...systemInfo(folder), children: [] }));
  return [...system, ...generalNodes(cache, null)];
}

export function selectStorageFolder(cache: MockCache, id: StorageFolderId): StorageFolderInfo | undefined {
  const system = SYSTEM_FOLDERS.find((folder) => folder.id === id);
  if (system !== undefined) {
    return systemInfo(system);
  }
  const folder = cache.storageFolders.get(id);
  return folder === undefined ? undefined : generalInfo(folder);
}

export function selectFolderAncestors(cache: MockCache, id: StorageFolderId): StorageFolderInfo[] {
  const ancestors: StorageFolderInfo[] = [];
  const seen = new Set<StorageFolderId>([id]);
  let parentId = cache.storageFolders.get(id)?.parentId ?? null;
  while (parentId !== null && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = cache.storageFolders.get(parentId);
    if (parent === undefined) {
      break;
    }
    ancestors.unshift(generalInfo(parent));
    parentId = parent.parentId;
  }
  return ancestors;
}

export function isGeneralFolder(cache: MockCache, id: StorageFolderId): boolean {
  return !isSystemFolderId(id) && cache.storageFolders.get(id)?.workspaceId === cache.activeWorkspaceId;
}

export function canEditFolder(cache: MockCache, id: StorageFolderId): boolean {
  return isGeneralFolder(cache, id);
}
